import type { IDVIRLog } from "./DVIRLog";

const NO_DEVICE_ID = "NoDeviceId";
const NO_TRAILER_ID = "NoTrailerId";

/**
 * Pure counterparts of DVIRLog.getResource().id and DVIRLog.needsRepair(), usable from a reducer (the
 * DVIRLog class pulls in the whole app). They must stay in step with the class; the parity test in
 * dvirRepair.test.ts compares them on the same logs.
 */
export function getLogResourceId(log: IDVIRLog): string {
    const trailerId = log.trailer?.id ?? NO_TRAILER_ID;
    return trailerId === NO_TRAILER_ID ? log.device?.id ?? NO_DEVICE_ID : trailerId;
}

/** Not certified, and at least one defect still NotRepaired (DVIRLog.needsRepair). */
export function logNeedsRepair(log: IDVIRLog): boolean {
    return log.certifyDate === undefined && !!log.dVIRDefects?.some(defect => defect.repairStatus === "NotRepaired");
}

/**
 * For one user's logs: the assets whose latest log (by dateTime, as DVIRLogsService.getLatestLog)
 * needs repair. Only assets that need repair are listed.
 */
export function getRepairNeededByAsset(logs: IIdDictionary<IDVIRLog> | undefined): IIdDictionary<boolean> {
    const latest: IIdDictionary<IDVIRLog> = {};
    Object.keys(logs ?? {}).forEach(id => {
        const log = (logs as IIdDictionary<IDVIRLog>)[id];
        const assetId = getLogResourceId(log);
        const current = latest[assetId];
        // Later logs win; equal or missing times keep the later entry, like the stable sort in getLatestLog.
        if (!current || (log.dateTime ?? "") >= (current.dateTime ?? "")) {
            latest[assetId] = log;
        }
    });
    return Object.keys(latest).reduce((needed, assetId) => {
        if (logNeedsRepair(latest[assetId])) {
            needed[assetId] = true;
        }
        return needed;
    }, {} as IIdDictionary<boolean>);
}

export function getRepairNeededByUser(logsByUser: IIdDictionary<IIdDictionary<IDVIRLog>> | undefined): IIdDictionary<IIdDictionary<boolean>> {
    return Object.keys(logsByUser ?? {}).reduce((byUser, userId) => {
        byUser[userId] = getRepairNeededByAsset((logsByUser as IIdDictionary<IIdDictionary<IDVIRLog>>)[userId]);
        return byUser;
    }, {} as IIdDictionary<IIdDictionary<boolean>>);
}
